import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Card, Layout, Result, Skeleton, Typography, theme } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { useFunctions } from 'reactfire'
import baseStyles from '../less/BaseLayout.module.less'
import helpStyles from '../components/HelpCenter/HelpCenter.module.less'
import {
  buildCategoryTree,
  helpLocale,
  onboardingHelpArticlePath,
  onboardingHelpPath,
  type HelpCategoryNode,
} from '../components/HelpCenter/helpTree'
import { renderHelpArticle } from '../components/HelpCenter/renderHelpMarkdown'
import type {
  HelpArticle,
  HelpArticleSummary,
  HelpCategory,
} from '../components/HelpCenter/types'
import { api } from '../lib/api'
import { logger } from '../lib/logger'
import { readOnboardingSessionToken } from '../lib/onboardingSessionToken'
import { useLogo } from '../lib/themeController'

interface CollectionResponse {
  categories: HelpCategory[]
  articles: HelpArticleSummary[]
}

type Loaded<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'denied' | 'missing' | 'failed' }

function failureOf(error: unknown): 'denied' | 'missing' | 'failed' {
  const code = String((error as { code?: unknown } | null)?.code ?? '')
  if (code.endsWith('permission-denied')) return 'denied'
  if (code.endsWith('not-found')) return 'missing'
  return 'failed'
}

// The guides are read with the onboarding link's own token: these pages are
// used by payroll accountants who have no login.
function useOnboardingHelp<T>(
  method: string,
  params: Record<string, string>
): Loaded<T> {
  const { companyUid } = useParams()
  const { i18n } = useTranslation()
  const functions = useFunctions()
  const locale = helpLocale(i18n.language)
  const key = JSON.stringify(params)
  const [state, setState] = useState<Loaded<T>>({ status: 'loading' })

  useEffect(() => {
    let current = true
    setState({ status: 'loading' })
    const sessionToken = readOnboardingSessionToken(companyUid)
    api(functions, method, {
      companyUid,
      locale,
      ...(sessionToken ? { sessionToken } : {}),
      ...JSON.parse(key),
    })
      .then((data) => {
        if (current) setState({ status: 'ready', data: data as T })
      })
      .catch((error: unknown) => {
        const status = failureOf(error)
        if (status === 'failed') {
          logger.warn('Onboarding help could not be loaded', {
            method,
            error: String(error),
          })
        }
        if (current) setState({ status })
      })
    return () => {
      current = false
    }
  }, [functions, method, companyUid, locale, key])

  return state
}

function Failure({ status }: { status: 'denied' | 'missing' | 'failed' }) {
  const { t } = useTranslation()
  if (status === 'missing') {
    return <Result status="404" title={t('frontend:helpCenter.notFound')} />
  }
  return (
    <Result
      status="warning"
      title={t(
        status === 'denied'
          ? 'frontend:helpCenter.sessionExpired'
          : 'frontend:helpCenter.loadFailed'
      )}
    />
  )
}

function Article() {
  const { companyUid = '', ref = '' } = useParams()
  const navigate = useNavigate()
  const request = useMemo(() => ({ ref }), [ref])
  const state = useOnboardingHelp<{ article: HelpArticle }>(
    'onboarding/help/get_article',
    request
  )
  const article = state.status === 'ready' ? state.data.article : null
  const rendered = useMemo(
    () =>
      article
        ? renderHelpArticle(article.content, null, (url) =>
            onboardingHelpPath(companyUid, url)
          )
        : null,
    [article, companyUid]
  )

  if (state.status === 'loading') {
    return <Skeleton active paragraph={{ rows: 8 }} />
  }
  if (!article || !rendered) {
    return (
      <Failure status={state.status === 'ready' ? 'missing' : state.status} />
    )
  }

  // Links to other guides stay inside the onboarding instead of reloading it.
  function onBodyClick(event: MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const anchor = (event.target as HTMLElement).closest?.('a[data-in-app]')
    const href = anchor?.getAttribute('href')
    if (!href) return
    event.preventDefault()
    navigate(href)
  }

  return (
    <>
      <Typography.Title level={2}>{article.title}</Typography.Title>
      <div
        className={helpStyles.articleBody}
        onClick={onBodyClick}
        dangerouslySetInnerHTML={{ __html: rendered.html }}
      />
    </>
  )
}

function Section({
  companyUid,
  node,
  top,
}: {
  companyUid: string
  node: HelpCategoryNode
  top?: boolean
}) {
  return (
    <div>
      <Typography.Title
        level={top ? 2 : 4}
        style={top ? undefined : { marginTop: 16 }}
      >
        {node.name}
      </Typography.Title>
      {top && node.description && (
        <Typography.Paragraph type="secondary">
          {node.description}
        </Typography.Paragraph>
      )}
      {node.articles.map((article) => (
        <Link
          key={article.id}
          to={onboardingHelpArticlePath(companyUid, article.slug)}
          className={helpStyles.articleLink}
        >
          {article.title}
        </Link>
      ))}
      {node.children.map((child) => (
        <Section key={child.id} companyUid={companyUid} node={child} />
      ))}
    </div>
  )
}

function Collection() {
  const { companyUid = '', slug = '' } = useParams()
  const request = useMemo(() => ({ slug }), [slug])
  const state = useOnboardingHelp<CollectionResponse>(
    'onboarding/help/get_collection',
    request
  )
  const tree = useMemo(
    () =>
      state.status === 'ready'
        ? buildCategoryTree({
            portal: 'customer',
            locale: 'de',
            categories: state.data.categories,
            articles: state.data.articles,
          })
        : [],
    [state]
  )

  if (state.status === 'loading') {
    return <Skeleton active paragraph={{ rows: 8 }} />
  }
  if (state.status !== 'ready' || tree.length === 0) {
    return (
      <Failure status={state.status === 'ready' ? 'missing' : state.status} />
    )
  }
  return (
    <>
      {tree.map((node) => (
        <Section key={node.id} companyUid={companyUid} node={node} top />
      ))}
    </>
  )
}

export function OnboardingHelpPage() {
  const { companyUid = '' } = useParams()
  const { t } = useTranslation()
  const { token } = theme.useToken()
  // useLogo is a plain JS hook; it returns the image URL.
  const logo = useLogo('full') as string

  return (
    <Layout className={baseStyles.baseLayoutOnboarding}>
      <Layout.Header
        className={baseStyles.header}
        style={{
          textAlign: 'center',
          marginTop: token.marginLG,
          marginBottom: token.marginSM,
        }}
      >
        <div className={baseStyles.topMenuOnboarding}>
          <div className={baseStyles.topMenuInner}>
            <img src={logo} alt="cleverlohn" className={baseStyles.logo} />
            <Link to={`/onboarding/${companyUid}`}>
              <ArrowLeftOutlined /> {t('frontend:helpCenter.backToOnboarding')}
            </Link>
          </div>
        </div>
      </Layout.Header>
      <Layout.Content
        style={{ width: 1024, margin: '0 auto', padding: '0 30px 30px' }}
      >
        <Card bordered={false}>
          <Routes>
            <Route path="a/:ref" element={<Article />} />
            <Route path="c/:slug" element={<Collection />} />
            <Route path="*" element={<Failure status="missing" />} />
          </Routes>
        </Card>
      </Layout.Content>
    </Layout>
  )
}
